/**
 * API-Schicht des Kündigungsregisters (Punkt 18).
 *
 * Eigene Datei aus demselben Grund wie beim Widerruf: der Vorgang gehört
 * **drei Bereichen gemeinsam** — Vertrieb versucht die Rückgewinnung, KB lässt
 * die Betreuung auslaufen, Finance beendet Abo und Rechnung.
 *
 * ⛔ Diese Schicht schreibt **keine** Kündigungs-Daten. `gekuendigt_am`/`_zum`
 * bleiben in HubSpot, das Vertragsende bleibt gerechnet.
 */

import type { AnpassungImErgebnis } from "./vertragsanpassung";
import { callBackend } from "./client";
// 🔑 Bericht, Verlaufspunkt und Zeitraum kommen aus dem Widerruf: es ist
// derselbe Rechenweg. *Ein zweiter Typ mit denselben Feldern wäre eine zweite
// Wahrheit über dieselbe Zahl.*
import type { QuotenAntwort, VerlaufPunkt, Zeitraum } from "./widerruf";

/** Gerechnet, nie gesetzt — die Spiegelung von `zustandVon` im Server. */
export type KuendigungZustand =
  | "anwaerter" | "in_restlaufzeit" | "ende_erreicht"
  | "abgeschlossen" | "storniert" | "unstimmig";

/**
 * 🔑 Die Beschriftung sagt, was **zu tun** ist, nicht was der Fall *ist*.
 * *Ein Zustandsname, der nur benennt, lässt den Leser die Handlung raten.*
 */
export const ZUSTAND_LABEL: Record<KuendigungZustand, string> = {
  /**
   * 🔴 Hiess bis zum Bildschirm-Blick am 21.09. **„Stichtag naht"** — und stand
   * damit auch an Büchen-Siebeneichener, dessen Stichtag im **April 2028**
   * liegt. Der Zustand `anwaerter` heisst „nicht gekündigt", nicht „bald".
   * Die Nähe trägt der ANWÄRTER-REITER (60-Tage-Fenster), nicht die Pille.
   * 🔑 *Ein Etikett, das mehr verspricht als der Zustand hergibt, macht aus
   * einer Sortierhilfe eine Falschauskunft.*
   */
  anwaerter: "nicht gekündigt",
  in_restlaufzeit: "läuft noch",
  ende_erreicht: "Ende erreicht — abwickeln",
  abgeschlossen: "abgeschlossen",
  storniert: "zurückgenommen",
  unstimmig: "widersprüchliche Daten"
};

export type Unstimmigkeit = { code: string; text: string };

export type Abweichung = {
  istEnde: string;
  sollEnde: string;
  tage: number;
  richtung: "spaeter" | "frueher" | "gleich";
  herleitung: string;
};

export type RegisterZeile = {
  fall_id: string;
  company_id: string;
  verein: string;
  zustand: KuendigungZustand;
  gekuendigt_am: string;
  gekuendigt_zum: string;
  ende_ist: string;
  stichtag_ist: string;
  ende_soll: string;
  stichtag_soll: string;
  herleitung: string;
  /**
   * 🆕 05.10.2026 — woher der Stichtag kommt (`widerruf` · `sonderkuendigung` ·
   * `initiallaufzeit` · `verlaengerung` · …), vom Server aus `berichtigtesEnde`.
   * ⚠️ Optional: ein älterer Server schickt es nicht — dann liest die Ansicht
   * die Art wie bisher aus dem Herleitungssatz.
   */
  stichtag_quelle?: string;
  abweichung: Abweichung | null;
  unstimmig: Unstimmigkeit[];
  /**
   * Was noch fehlt, damit der Fall schliesst: die drei Kenntnisnahmen
   * (Vertrieb · KB · Finance) **und** die Bestätigung an den Verein.
   * ⚠️ Leer, solange es zum Verein gar keinen Vorgang gibt — dort ist nichts
   * offen, sondern noch nichts begonnen.
   */
  offene_riegel: string[];
  /**
   * 🆕 26.09.2026 — der Riegelstand als **Werte** (Datum oder leer). Er trägt
   * das Rollen-Band der Übersicht: `offene_riegel` sind Beschriftungen und
   * beantworten nur „was fehlt", nicht „wen geht es an".
   */
  riegel: {
    rueckgewinnung: string;
    /** Nur bei `laeuft` gesetzt — der Termin, an dem entschieden wird. */
    rueckgewinnung_wiedervorlage: string;
    vertrieb: string;
    kb: string;
    finance: string;
    bestaetigung: string;
    storniert: string;
    abgeschlossen: string;
  };
  bearbeiter: string;
  grund: string;
  regel_hinterlegt: boolean;
  regel_grund: string;
  tage_bis_stichtag: number | null;
  /**
   * Der Ausstieg wirkt **vor** dem Vertragsbeginn ⇒ Widerruf, nicht Kündigung.
   * Die Zeile steht dann im Archiv und sagt dort, warum.
   * 📏 33 Zeilen dieser Form standen am 21.09.2026 unter „laufend".
   */
  /**
   * **Testverein** (Glattzug E16). Zählt in **keiner** Kachel und keinem
   * Reiter mit, steht blass am Ende der Liste und trägt die Pille „Testverein".
   * 🔴 Quelle ist das Register `ve_testverein`, **nicht** der Name — die
   * Begründung steht im Backend-Typ (`kuendigung-faelle.ts`).
   */
  ist_testverein: boolean;
  widerruf_vor_start: boolean;
  /**
   * 🆕 26.09.2026 — der Beleg dazu, wenn es einen gibt.
   * ⚠️ Leer heisst **„geraten, nicht belegt"**, nicht „kein Widerruf": seit
   * heute sticht ein Widerrufsfall mit beendendem Ergebnis die alte Regel
   * (Ausstieg vor Vertragsbeginn). Ohne diese ID stünde auf solchen Karten der
   * Satz „Der Ausstieg wirkt vor dem Vertragsbeginn" — und der ist dort falsch.
   * 🔑 *Die richtige Einsortierung mit der falschen Begründung ist ein eigener
   * Fehler.*
   */
  widerruf_fall_id: string;
  /**
   * 🆕 28.09.2026 — **welcher der drei Belege** die Einordnung trägt.
   * Der Server entscheidet in dieser Rangfolge:
   *   · `"fall"`      — ein Widerrufsfall mit beendendem Ergebnis (`widerruf_fall_id`)
   *   · `"hs_marker"` — HubSpot trägt `lead_status = "Widerruf vor Start"`
   *   · `"vor_start"` — nur die Signatur: Ausstieg ≤ Vertragsbeginn
   *   · `""`          — kein Widerruf
   *
   * 📏 Gemessen 28.09.2026: von den 18 Zeilen nach der Grenze trugen **10** nur
   * die Signatur — und **alle zehn** stehen in HubSpot ausdrücklich als
   * »Widerruf vor Start«. Der Satz „Kein Widerrufsfall gefunden" behauptete
   * eine Lücke, wo eine Auskunft lag.
   * 🔑 *Ein Feld, das nur »belegt/geraten« kennt, kann den mittleren Fall nicht
   * aussprechen — also erzählt es ihn als den schlechteren von beiden.*
   */
  widerruf_herkunft: "fall" | "hs_marker" | "vor_start" | "";
};

export type KuendigungModus = "off" | "dryrun" | "on";

export type KuendigungRegister = {
  heute: string;
  modus: KuendigungModus;
  /**
   * Wer die **Kündigungsbestätigung** an den Verein schickt — der Wert von
   * `KUENDIGUNG_BESTAETIGUNG_WER`, nicht sein Name (Glattzug K7).
   * ⚠️ **Leer heisst niemand**, nicht „unbekannt": der Server ist fail-closed.
   */
  bestaetigung_wer: string;
  laufend: RegisterZeile[];
  anwaerter: RegisterZeile[];
  archiv: RegisterZeile[];
  unstimmig: RegisterZeile[];
  abweichungen: RegisterZeile[];
  zaehler: {
    laufend: number; anwaerter: number; archiv: number;
    unstimmig: number; abweichungen: number;
  };
};

export type KuendigungFall = {
  fall_id: string;
  company_id: string;
  verein: string;
  gekuendigt_am: string;
  ende_bei_anlage: string;
  quelle: string;
  grund: string;
  art: string;
  bearbeiter: string;
  angefasst_am: string;
  quittung_vertrieb: string;
  quittung_kb: string;
  quittung_finance: string;
  /**
   * 🆕 26.09.2026 — **wer** quittiert hat. Bis dahin zeigte die Karte nur das
   * Datum, obwohl D1 den Namen führt. 🔑 *Der Wert liegt an der Quelle und
   * fällt eine Ebene später hin.*
   */
  quittung_vertrieb_von: string;
  quittung_kb_von: string;
  quittung_finance_von: string;
  /**
   * Der VIERTE Riegel. Er heisst anders als die drei davor, weil er etwas
   * anderes ist: 🔑 *ein Versand an einen Vertragspartner und ein Haken im
   * eigenen Haus sind nicht dieselbe Handlung.*
   */
  bestaetigung_am: string;
  bestaetigung_an: string;
  bestaetigung_von: string;
  /** '' | versucht_erfolglos | nicht_versucht | zurueckgewonnen */
  rueckgewinnung: string;
  rueckgewinnung_notiz: string;
  /** Nur bei `laeuft` gesetzt — der Termin, an dem weitergesprochen wird. */
  rueckgewinnung_wiedervorlage: string;
  /**
   * 🆕 28.09.2026 (Migration 0134) — **der Eingang, wie beim Widerruf.**
   * ⚠️ Leer heisst „über den Abgleich erkannt", nicht „vergessen": der
   * HubSpot-Weg kennt den Eingangsweg nicht und kann ihn nicht kennen.
   */
  eingang_weg: string;
  eingang_absender: string;
  /** **Der Beleg** — vollständig, nicht zusammengefasst. */
  wortlaut: string;
  abgeschlossen_am: string;
  storniert_am: string;
  storno_grund: string;
  /**
   * ⑤ Was beim Anlegen der HubSpot-Aufgaben schiefging — im Klartext.
   * 🔑 *Ein Fall ohne Aufgabe darf nicht aussehen wie ein Fall mit Aufgabe.*
   * Dazu gehört der stillste Fall: eine Aufgabe **ohne Besitzer** wird angelegt
   * und ist in HubSpot praktisch unsichtbar.
   */
  hs_fehler: string;
  /**
   * 🆕 26.09.2026 — die IDs der vier HubSpot-Aufgaben.
   * ⛔ Leer ist **kein Fehler**, sondern „noch nicht" oder „schon geschlossen" —
   * die Anzeige nennt deshalb bei jeder leeren ID den **Zeitpunkt**, an dem sie
   * entstehen würde. 🔑 *Ein „nicht angelegt" ohne das Wann liest sich als
   * Versäumnis.*
   */
  hs_task_vertrieb: string;
  hs_task_kb: string;
  hs_task_finance: string;
  hs_task_bestaetigung: string;
  /**
   * 🆕 26.09.2026 — **der HubSpot-Vorzustand** (B6, Migration 0131). Der Server
   * schickt ihn seit demselben Tag mit; hier fehlte er, also zeigte ihn niemand.
   * 🔑 *Dieselbe Naht wie bei den Aufgaben-IDs, nur eine Ebene höher: ein Typ,
   * der ein Feld nicht kennt, macht es unsichtbar, ohne dass etwas ausfällt.*
   *
   * ⚠️ **Leer heisst „nicht bekannt", nicht „war leer".** Das unterscheidet
   * `vorzustand_am`: mit Zeitstempel und leerem Wert **war** der Wert in HubSpot
   * leer. 🔑 *„Kein Vorzustand" und „Vorzustand war leer" dürfen nicht gleich
   * aussehen* — der Rückweg hängt daran.
   */
  vorzustand_lifecycle: string;
  vorzustand_lead_status: string;
  vorzustand_am: string;
};

/** Die drei Riegel, die eine Kenntnisnahme sind. */
export type Riegel = "vertrieb" | "kb" | "finance";

export const RIEGEL_LABEL: Record<Riegel, string> = {
  vertrieb: "Vertrieb", kb: "Kundenbetreuung", finance: "Finance"
};

export const RUECKGEWINNUNG_OPTIONEN: ReadonlyArray<{ wert: string; label: string; hinweis: string }> = [
  {
    wert: "zurueckgewonnen", label: "zurückgewonnen",
    hinweis: "Der Verein bleibt. ⚠️ Die Kündigung in HubSpot muss trotzdem zurückgenommen werden — das Register ändert die Quelle nicht."
  },
  {
    // ⚠️ 28.09.2026 umbenannt, Justins Worte: *„bleibt bestehen oder halt
    // rückgewonnen"*. 🔑 *„Erfolglos" bewertet den, der es versucht hat;
    // „bleibt bestehen" sagt, was mit dem Vertrag ist.* Der Schlüssel bleibt.
    wert: "versucht_erfolglos", label: "Kündigung bleibt bestehen",
    hinweis: "Es wurde nachgefasst, der Verein bleibt bei der Kündigung."
  },
  {
    wert: "nicht_versucht", label: "bewusst nicht versucht",
    hinweis: "Kein Versuch — und das ist eine Entscheidung, keine Lücke. Der Grund gehört in die Notiz."
  },
  {
    wert: "laeuft", label: "Entscheidung steht aus",
    hinweis: "Gesprochen, nichts entschieden. ⚠️ Der Fall bleibt OFFEN, bis der Endstand da ist — er lässt sich nicht abschliessen. Braucht eine Wiedervorlage."
  }
];

/**
 * Welche Werte einen **Endstand** bedeuten. `laeuft` gehört ausdrücklich nicht
 * dazu: es trägt eine Wiedervorlage und holt den Fall am Stichtag zurück.
 * 🔑 *Ein Zwischenstand ohne Wiedervorlage ist ein Endstand, der sich nicht als
 * einer zu erkennen gibt.*
 */
export const RUECKGEWINNUNG_BRAUCHT_TERMIN = "laeuft";

export type Vertragsregel = {
  verlaengerungMonate: string;
  fristMonate: string;
  pauseVerlaengert: string;
  pauseMonate: string;
  festesVertragsende: string;
  /**
   * 🆕 04.10.2026 (Migration 0138) — ein **hinterlegtes**
   * Sonderkündigungsrecht, `YYYY-MM-DD`.
   * ⚠️ Leer heisst **nicht hinterlegt**, nicht „kein SKR": dann gilt wieder der
   * Wert aus den Vereinsdaten (HubSpot-Spiegel).
   */
  sonderkuendigungBis: string;
  manuell: boolean;
  grund: string;
  gesetztAm: string;
  gesetztVon: string;
};

export type VerlaufEintrag = { art: string; text: string; akteur: string; ms: number };

/** Antwortform aller schreibenden Vorgänge — Fall + Hinweise im Klartext. */
export type VorgangAntwort = { fall: KuendigungFall; hinweise: string[] };

export const kuendigungUebersicht = () =>
  callBackend<KuendigungRegister>("/api/v1/kuendigung/uebersicht", {});

/**
 * 🆕 **Die Zeitraum-Liste** (01.10.2026) — deckungsgleich zur Closingliste.
 *
 * ⚠️ Zeitraum-Anker ist `gekuendigt_am`. Sie liest dasselbe Register wie die
 * Übersicht: 🔑 *zwei Abfragen auf denselben Gegenstand ergeben zwei Zahlen,
 * und die sichtbarere gewinnt.*
 */
export type KuendigungListe = {
  heute: string;
  zeilen: RegisterZeile[];
  zeitraum: {
    start_date: string; end_date: string; label: string; slug: string;
    previous: { start_date: string; end_date: string } | null;
  };
  ausserhalb: number;
  ohne_datum: number;
};

export const kuendigungListe = (p: { range?: string; start_date?: string; end_date?: string }) =>
  callBackend<KuendigungListe>("/api/v1/kuendigung/liste", p);

/**
 * ═══ DIE VORGANGS-AUFRUFE — seit dem 21.09.2026 mit Rufer ══════════════════
 *
 * Sie standen hier schon einmal und wurden **entfernt**, weil `tote-exporte`
 * sie zu Recht meldete: 🔑 *ein Export, den niemand ruft, ist kein Bauteil,
 * sondern eine Absicht.* Jetzt ruft `kuendigung-detail.ts` sie — Aufruf und
 * Knopf sind zusammen entstanden, nicht nacheinander.
 */

/**
 * ⑦ Der Stripe-Abgleich — gerechnet, nicht erinnert.
 *
 * 🔑 *Eine Erinnerung verfällt, ein Abgleich nicht.* `sollEnde` ist
 * `gekuendigt_zum + 1 Tag`, weil Stripes Phasenende **exklusiv** zählt.
 * ⚠️ **Nicht ±1**: eine Toleranz in beide Richtungen schluckt genau die
 * Abweichung, für die sie gebaut wurde (📏 2 von 3 echten Funden, 21.09.2026).
 */
export type StripeLage = {
  phaseEnde: string;
  sollEnde: string;
  tage: number | null;
  zustand: "deckt" | "weicht_ab" | "kein_abo_ende" | "nicht_rechenbar";
  text: string;
};

/**
 * 🆕 26.09.2026 — **was die Automatik heute sagt**, neben dem, was hinterlegt ist.
 *
 * 🔴 Das Häkchen „Pausenzeit hinten dranhängen" braucht beides. `pauseVerlaengert`
 * in der Regel kennt DREI Zustände (`ja` · `nein` · leer ⇒ Stichtagsregel), ein
 * Häkchen zwei. 🔑 ***Aus „nicht hinterlegt" darf nie „ist so entschieden"
 * werden*** — sonst steht ein Haken da, den niemand gesetzt hat.
 */
export type RegelAutomatik = {
  /** Was `pauseVerlaengertFuer` heute ergibt — auch wenn nichts hinterlegt ist. */
  pauseVerlaengert: boolean;
  /** Woran das hängt: der Vertragsabschluss des Vereins. Leer ⇒ nicht entscheidbar. */
  vertragsabschluss: string;
  /** Der Stichtag der Regel. Leer ⇒ gilt für alle Verträge (Altverhalten). */
  pauseAbUnterschrift: string;
  verlaengerungMonate: number;
  fristMonate: number;
};

/**
 * 🆕 26.09.2026 — **die Uhr des Vorgangs**: das *wirksame* Vertragsende.
 *
 * 🔴 Der Server rechnete es längst (für den Stripe-Abgleich) und schickte es
 * nicht mit. Die Ansicht kannte nur `ende_bei_anlage` — den Stand vom Tag der
 * Anlage. ⚠️ 📏 B1 hat am 25.09.2026 **24 Vertragsenden verschoben**.
 * 🔑 *Ein Wert, der für eine Nebenrechnung geholt wird, ist deshalb noch keine
 * Auskunft an den Leser.*
 */
export type FallUhr = {
  /** `gekuendigt_zum` aus dem Stamm, sonst `ende_bei_anlage`. */
  wirksamesEnde: string;
  endeBeiAnlage: string;
  /** Leer ⇒ der Stamm führt kein Ende, `wirksamesEnde` ist dann geerbt. */
  ausStamm: string;
  heute: string;
};

export type FallAntwort = {
  /** 🆕 06.10.2026 — Personalnummer → Name für die Personen dieses Falls. Optional (älterer Server). */
  namen?: Record<string, string>;
  fall: KuendigungFall;
  verlauf: VerlaufEintrag[];
  regel: Vertragsregel;
  stripe: StripeLage;
  automatik: RegelAutomatik;
  uhr: FallUhr;
  bestaetigung_versand: BestaetigungVersand;
};

/**
 * Wer die Kündigungsbestätigung **senden** darf, wer sie von Hand **festhalten**
 * darf, und ob der Versand überhaupt eingerichtet ist.
 *
 * 🔴 📏 Gemessen 29.09.2026: der Widerruf liefert das seit dem 25.09., die
 * Kündigung lieferte **nichts** davon — der Knopf stand allen offen und
 * antwortete erst **beim Klick** mit 403.
 * 🔑 ***Ein Knopf, der erst beim Drücken Nein sagt, ist keine Auskunft, sondern
 * eine Falle*** — und hier ist der Klick genau die Handlung, die man nicht
 * probehalber tut.
 *
 * ⚠️ `darf` und `darf_von_hand` sind **nicht** dasselbe (Justin 28.09.:
 * *„von hand sollte jeder bestätigen können von mir claudius und maxi"*).
 * 🔑 *Senden und Behaupten sind zwei verschiedene Handlungen.*
 * ⚠️ `eingerichtet` ist getrennt von `darf`: fehlt der Gmail-Zugang, sieht auch
 * der Berechtigte, WARUM nichts geht — statt eines Knopfes, der nichts tut.
 */
export type BestaetigungVersand = {
  darf: boolean;
  darf_von_hand: boolean;
  wer: string;
  absender: string;
  eingerichtet: boolean;
  grund: string;
};

export const kuendigungFall = (fallId: string) =>
  callBackend<FallAntwort>("/api/v1/kuendigung/fall", { fall_id: fallId });

/**
 * ⛔ **`/kuendigung/eroeffnen` hat hier bewusst KEINEN Client-Aufruf.**
 *
 * Der Endpunkt existiert und ist auditiert, aber Vorgänge entstehen
 * ausschliesslich über die beiden Wege, die Justin am 21.09.2026 benannt hat:
 * die Kündigung in der App eintragen (`KB.verein.kuendigung`) oder den
 * HubSpot-Übergang. Einen „Vorgang eröffnen"-Knopf im Register hat er
 * ausdrücklich abgelehnt (*„gar nicht — nur was ab jetzt kommt"*).
 *
 * 📏 Ich hatte den Aufruf trotzdem geschrieben; `tote-exporte` hat ihn am
 * 21.09. gemeldet — **nach** Commit und Deploy, weil ich nach den
 * Frontend-Änderungen nur die Frontend-Suite laufen liess und dieser Wächter
 * im BACKEND-Lauf sitzt.
 * 🔑 *Zwei Suiten heissen zwei Läufe — und der Wächter über die Frontend-Exporte
 * steht ausgerechnet im anderen.*
 */

export const kuendigungBearbeiter = (fallId: string, name: string, owner = "") =>
  callBackend<VorgangAntwort>("/api/v1/kuendigung/bearbeiter",
    { fall_id: fallId, name, owner });

export const kuendigungGrund = (fallId: string, grund: string, art: string) =>
  callBackend<VorgangAntwort>("/api/v1/kuendigung/grund",
    { fall_id: fallId, grund, art });

export const kuendigungQuittieren = (fallId: string, riegel: Riegel) =>
  callBackend<VorgangAntwort>("/api/v1/kuendigung/quittieren",
    { fall_id: fallId, riegel });

/**
 * 🆕 **29.09.2026 — der Grund ist Pflicht** (Posten 5). 📏 Bis heute stand im
 * Verlauf `vertrieb zurückgenommen.` — warum, stand nirgends.
 * 🔑 *Eine Rücknahme ohne Grund ist von einem Fehlgriff nicht zu unterscheiden.*
 */
export const kuendigungQuittungZurueck = (fallId: string, riegel: Riegel, grund: string) =>
  callBackend<VorgangAntwort>("/api/v1/kuendigung/quittung-zurueck",
    { fall_id: fallId, riegel, grund });

/**
 * 🆕 **Das Rückgewinnungs-Ergebnis zurücknehmen** (Posten 5, 29.09.2026).
 *
 * 📏 Den Weg gab es bei der Kündigung nicht — man überschrieb, ohne Grund, und
 * die Vertriebs-Quittung blieb stehen.
 * ⚠️ **Nur Bereichsleitung**, wie beim Widerruf: *ein Rückweg, der so leicht zu
 * gehen ist wie der Hinweg, ist kein Rückweg, sondern ein zweiter Hinweg.*
 */
export const kuendigungRueckgewinnungZurueck = (fallId: string, grund: string) =>
  callBackend<VorgangAntwort>("/api/v1/kuendigung/rueckgewinnung-zurueck",
    { fall_id: fallId, grund });

export const kuendigungRueckgewinnung = (
  fallId: string, ergebnis: string, notiz: string, wiedervorlage = "",
  /** 🆕 05.10.2026 (K9): die Vertragsanpassung im selben Schritt — der Server prüft sie VOR dem Ergebnis. */
  anpassung?: AnpassungImErgebnis
) =>
  callBackend<VorgangAntwort & { anpassung?: { vorgang_id: string; bestaetigt: boolean; grund: string } | null }>("/api/v1/kuendigung/rueckgewinnung",
    { fall_id: fallId, ergebnis, notiz, wiedervorlage, ...(anpassung ? { anpassung } : {}) });

/**
 * Hält **fest**, versendet nicht — für den Fall, dass die Bestätigung auf
 * einem anderen Weg rausging (Telefon, eigenes Postfach, Brief).
 * Der Empfänger ist Pflicht: *ein Haken ohne Adresse beantwortet nicht, wer
 * Bescheid weiss.*
 *
 * 🆕 **29.09.2026 — und der Nachweis dazu.** Bis heute genügte der Empfänger.
 * 🔑 ***Der Riegel, der den Vorgang schliesst, war damit eine Behauptung ohne
 * Nachweis*** — gegenüber einem zahlenden Verein. Jetzt verlangt er dasselbe
 * wie der Widerruf seit dem 18.09.: Beleg, Wortlaut und die ausdrückliche
 * Erklärung, dass die Mail raus ist.
 */
export const kuendigungBestaetigung = (
  fallId: string,
  e: { an: string; beleg: string; mailtext: string; erklaert: boolean }
) =>
  callBackend<VorgangAntwort>("/api/v1/kuendigung/bestaetigung",
    { fall_id: fallId, ...e });

export type BestaetigungEntwurf = {
  an: string; betreff: string; text: string;
  unterzeichner: string; absender: string; hinweise: string[];
};

/**
 * Schritt 1 des Versands — der Entwurf. **Schreibt nichts, sendet nichts.**
 *
 * 🔑 *Ein Knopf, der eine Erklärung an einen Vertragspartner sofort absetzt,
 * hat keinen Rückweg.* Der Dialog ist deshalb nicht Komfort, sondern der
 * Rückweg selbst — er liegt VOR der Handlung, weil es danach keinen gibt.
 */
export const kuendigungBestaetigungEntwurf = (fallId: string) =>
  callBackend<BestaetigungEntwurf>("/api/v1/kuendigung/bestaetigung-entwurf",
    { fall_id: fallId });

/**
 * 🆕 **Die PROBE des Versandwegs** (Posten 13, 02.10.2026).
 *
 * **Fasst keinen Fall an und erreicht keinen Verein** — es gibt **kein**
 * Empfängerfeld, die Testmail geht an das Absender-Postfach selbst.
 * 🔑 *Eine Prüffunktion mit freiem Empfängerfeld ist irgendwann der bequemste
 * Weg, eine Mail zu verschicken — und dann ist sie keine Prüffunktion mehr.*
 *
 * `mail: false` prüft nur den Token-Tausch (also die Delegierung), `true`
 * schickt zusätzlich eine Probe ans eigene Postfach.
 *
 * 📏 Die Paritätsliste vom 29.09.2026 führte sie als **Ⓐ**: beim Widerruf
 * vorhanden, in der Kündigung fehlend — *„ob der Versandweg steht, erfährt man
 * erst am echten Verein."*
 * ⚠️ Dieselbe Diagnose wie beim Widerruf, **nicht** eine zweite: beide Register
 * teilen Postfach, Absender und Dienstkonto. *Eine zweite Diagnose über
 * denselben Weg verfällt ungleichzeitig mit der ersten.*
 * ⚠️ Ein `403` heisst „du darfst die Probe nicht", **nicht** „der Weg ist
 * kaputt" — sie hängt hinter demselben Riegel wie der Versand.
 */
export const kuendigungBestaetigungProbe = (mail: boolean) =>
  callBackend<{
    ok: boolean; konto: string; postfach: string; quelle: string; meldung: string;
    /** Die Überschrift — kommt vom SERVER, aus derselben Quelle wie `meldung`. */
    titel: string;
  }>("/api/v1/kuendigung/bestaetigung-probe", { mail });

/**
 * Schritt 2 — senden. Es geht **der Text raus, der am Bildschirm stand**:
 * der Server baut ihn nicht neu.
 */
export const kuendigungBestaetigungSenden = (
  fallId: string, e: { an: string; betreff: string; text: string }
) => callBackend<VorgangAntwort>("/api/v1/kuendigung/bestaetigung-senden",
  { fall_id: fallId, ...e });

/**
 * 🆕 **Übernehmen — „ich bin dran"** (Posten 8, 01.10.2026).
 *
 * 📏 Gemessen 29.09.: der Weg fehlte; `angefasst_am` entstand nur als
 * Nebenwirkung, wenn man sich selbst als Bearbeiter eintrug.
 * 🔑 ***Zwei Leute rufen denselben Verein an, ohne es zu sehen.***
 * ⛔ Setzt **keinen** Bearbeiter: übernehmen und zuständig sein sind zwei
 * Aussagen.
 */
export const kuendigungUebernehmen = (fallId: string) =>
  callBackend<VorgangAntwort>("/api/v1/kuendigung/uebernehmen", { fall_id: fallId });

/**
 * 🆕 **Die Bestätigung zurücknehmen** (Posten 8, 01.10.2026) — nur BL, Grund Pflicht.
 *
 * 🔑 ***Ein irrtümlich gesetzter vierter Riegel schliesst den Fall*** — und
 * zurück ging bisher nur der Storno, der etwas anderes bedeutet.
 * ⚠️ Beleg und Wortlaut bleiben stehen: *ein Rückweg, der die Begründung des
 * Hinwegs löscht, macht den Irrtum unauffindbar.*
 */
export const kuendigungBestaetigungZurueck = (fallId: string, grund: string) =>
  callBackend<VorgangAntwort>("/api/v1/kuendigung/bestaetigung-zurueck",
    { fall_id: fallId, grund });

/**
 * 🆕 **Die Erfassung berichtigen** (Posten 8, 01.10.2026) — nur BL, Grund Pflicht.
 *
 * ⛔ **Ohne das Kündigungsdatum.** `gekuendigt_am` ist der Vorgangsschlüssel und
 * stammt aus HubSpot — falsches Datum heisst: dort berichtigen, hier stornieren
 * und neu. *Kein Mangel gegenüber dem Widerruf, sondern der Unterschied
 * zwischen einem selbst erfassten und einem gespiegelten Vorgang.*
 *
 * ⚠️ Ein Feld weglassen heisst „nicht anfassen", `""` heisst „leeren".
 */
export const kuendigungErfassungKorrigieren = (
  fallId: string,
  grund: string,
  k: { eingang_weg?: string; eingang_absender?: string; wortlaut?: string }
) => callBackend<VorgangAntwort>("/api/v1/kuendigung/erfassung-korrigieren",
  { fall_id: fallId, grund, ...k });

export const kuendigungStorno = (fallId: string, grund: string) =>
  callBackend<VorgangAntwort>("/api/v1/kuendigung/storno",
    { fall_id: fallId, grund });

export const kuendigungAbschlussPruefen = (fallId: string) =>
  callBackend<VorgangAntwort>("/api/v1/kuendigung/abschluss-pruefen",
    { fall_id: fallId });

/** `undefined` heisst „unverändert", `''` heisst „zurück auf Automatik". */
export const kuendigungRegelSetzen = (a: {
  company_id: string; verein: string; grund: string;
  verlaengerung_monate?: string; frist_monate?: string;
  pause_verlaengert?: string; pause_monate?: string; festes_vertragsende?: string;
  /** 🆕 04.10.2026 — hinterlegtes SKR, `YYYY-MM-DD`. `""` = zurueck auf den Stammwert. */
  sonderkuendigung_bis?: string;
}) => callBackend<{ regel: Vertragsregel; geaendert: string[] }>(
  "/api/v1/kuendigung/regel-setzen", { ...a });

/**
 * 🆕 28.09.2026 — **die Regel eines Vereins, ohne Vorgang.**
 *
 * 📏 Gemessen am 28.09.2026: das Register führte **zwei** Vorgänge, und
 * **keiner** der sechs Vereine, die eine Regel brauchten, hatte einen.
 * 🔑 ***Eine Bedienform am Vorgang erreicht nur Vereine, bei denen schon
 * gekündigt wurde — aber eine Regel legt man fest, BEVOR gekündigt wird.***
 *
 * ⚠️ `automatik` kommt mit, weil das Häkchen sonst seinen **geerbten** Stand
 * nicht zeigen kann — und ein Häkchen ohne Herkunft behauptet eine Entscheidung.
 */
export type RegelVerein = {
  company_id: string;
  verein: string;
  vertragsabschluss: string;
  vertragsstart: string;
  vertragsende: string;
  initiallaufzeit_monate: string;
  naechstes_ende: string;
  stichtag: string;
  quelle: string;
};

export type RegelAntwort = {
  regel: Vertragsregel;
  /** 🆕 05.10.2026 — Anzeigename zu `regel.gesetztVon`; leer = nicht aufgelöst (alter Server, Lesefehler). */
  gesetzt_von_name?: string;
  automatik: RegelAutomatik;
  verein: RegelVerein;
};

export const kuendigungRegel = (companyId: string) =>
  callBackend<RegelAntwort>("/api/v1/kuendigung/regel", { company_id: companyId });

/**
 * DIE KÜNDIGUNGSQUOTE — Antwort zu `POST /api/v1/kuendigung/quote`.
 *
 * 🔑 **Bericht und Verlauf sind dieselben Typen wie beim Widerruf**, weil es
 * derselbe Rechenweg ist (`baueQuotenAusMarkierung`). *Ein zweiter Typ mit
 * denselben Feldern wäre eine zweite Wahrheit über dieselbe Zahl.*
 *
 * Nur das letzte Feld ist eigen: `gekuendigteGesamt` statt `echteGesamt` /
 * `roheGesamt` — beim Widerruf misst der Abstand zwischen roh und echt einen
 * Befund, hier gibt es keinen rohen Wert.
 */
export type KQuotenAntwort = {
  bericht: QuotenAntwort["bericht"];
  verlauf: VerlaufPunkt[];
  verlaufJe: Record<string, VerlaufPunkt[]>;
  /** Vereine im Bestand, die als gekündigt gelten (ohne Widerrufe). */
  gekuendigteGesamt: number;
  heute: string;
  zeitraum: Zeitraum;
  granular: "m" | "w";
  vorlagen: Zeitraum[];
};

export const kuendigungQuote = (p: { von?: string; bis?: string; monat?: string } = {}) =>
  callBackend<KQuotenAntwort>("/api/v1/kuendigung/quote", p);
